/**
 * Shared ethers.js utilities for the frontend
 * These match the ethers v6 API and can be shared with the simulator
 */

import { ethers, TransactionResponse, TransactionReceipt, Provider } from 'ethers';

/**
 * Format a bigint value from wei to ether (or other 18-decimal token)
 */
export function formatEther(value: bigint | string | number): string {
  return ethers.formatEther(value);
}

/**
 * Parse an ether string to wei as bigint
 */
export function parseEther(value: string): bigint {
  return ethers.parseEther(value);
}

/**
 * Check if a string is a valid Ethereum address
 */
export function isAddress(value: string): boolean {
  return ethers.isAddress(value);
}

/**
 * Get a checksummed address
 */
export function getAddress(value: string): string {
  return ethers.getAddress(value);
}

/**
 * Format a bigint to a fixed number of decimal places for display
 */
export function formatEtherFixed(value: bigint, decimals: number = 4): string {
  const formatted = ethers.formatEther(value);
  return parseFloat(formatted).toFixed(decimals);
}

/**
 * Format a bigint value from wei to ether with max 5 decimal truncation
 */
export function formatEtherLimited(value: bigint | string | number): string {
  const formatted = parseFloat(ethers.formatEther(value));
  if (formatted === 0) return '0';
  if (formatted < 0.00001) return '<0.00001';
  const truncated = Math.floor(formatted * 100000) / 100000;
  // Remove trailing zeros
  return truncated.toFixed(5).replace(/\.?0+$/, '') || '0';
}

/**
 * WELEPHANT token decimals (9 decimals, matching mainnet)
 */
export const WELEPHANT_DECIMALS = 9;

/**
 * stWELEPHANT vault share token decimals (ERC4626, 18 decimals)
 */
export const STWELEPHANT_DECIMALS = 18;

/**
 * BNB native token decimals (18 decimals)
 */
export const BNB_DECIMALS = 18;

/**
 * Format a bigint value from WELEPHANT's smallest unit to human-readable
 * WELEPHANT uses 9 decimals (not 18 like ETH/BNB)
 */
export function formatWelephant(value: bigint | string | number): string {
  return ethers.formatUnits(value, WELEPHANT_DECIMALS);
}

/**
 * Parse a WELEPHANT string to its smallest unit as bigint
 */
export function parseWelephant(value: string): bigint {
  return ethers.parseUnits(value, WELEPHANT_DECIMALS);
}

/**
 * Format a bigint WELEPHANT value to a fixed number of decimal places for display
 */
export function formatWelephantFixed(value: bigint, decimals: number = 2): string {
  const formatted = ethers.formatUnits(value, WELEPHANT_DECIMALS);
  return parseFloat(formatted).toFixed(decimals);
}

/**
 * Format a bigint WELEPHANT value with max 5 decimal truncation
 */
export function formatWelephantLimited(value: bigint | string | number): string {
  const formatted = parseFloat(ethers.formatUnits(value, WELEPHANT_DECIMALS));
  if (formatted === 0) return '0';
  if (formatted < 0.00001) return '<0.00001';
  const truncated = Math.floor(formatted * 100000) / 100000;
  // Remove trailing zeros
  return truncated.toFixed(5).replace(/\.?0+$/, '') || '0';
}

/**
 * Format a bigint token amount for display with comma delimiting.
 * Truncates to max 5 decimal places. Strips trailing zeros.
 *   < 0.00001 → "<0.00001"
 *   < 1       → "0.12345"
 *   >= 1      → "1,234.56789"
 */
export function formatTokenAmount(amount: bigint, decimals: number): string {
  const value = parseFloat(ethers.formatUnits(amount, decimals));
  if (value === 0) return '0';
  if (value < 0.00001) return '<0.00001';
  const truncated = Math.floor(value * 100000) / 100000;
  if (truncated < 1) return truncated.toFixed(5).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '') || '0';
  const formatted = truncated.toLocaleString(undefined, { maximumFractionDigits: 5 });
  if (!formatted.includes('.')) return formatted;
  return formatted.replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * Format stWELEPHANT amount for display (18 decimals, comma-delimited)
 */
export function formatStWelephant(value: bigint): string {
  return formatTokenAmount(value, STWELEPHANT_DECIMALS);
}

/**
 * Format a USD value with 2 decimal truncation (not rounded)
 */
export function formatUsd(value: number | string): string {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (num === 0 || isNaN(num)) return '0.00';
  if (num < 0.01) return '<0.01';
  const truncated = Math.floor(num * 100) / 100;
  return truncated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Unicode subscript digits for formatting small prices
 */
const SUBSCRIPT_DIGITS = ['₀', '₁', '₂', '₃', '₄', '₅', '₆', '₇', '₈', '₉'];

/**
 * Convert a number to subscript Unicode characters
 */
function toSubscript(num: number): string {
  return num.toString().split('').map(d => SUBSCRIPT_DIGITS[parseInt(d)]).join('');
}

/**
 * Format a price for display.
 * Uses subscript notation for very small prices with many leading zeros.
 *
 * For prices with more than 3 leading zeros after the decimal, displays as:
 *   $0.0₇5274 (meaning 7 zeros total, then significant digits)
 *
 * Always shows at least 4 significant digits after any leading zeros.
 *
 * Examples:
 *   123.45 → $123.45
 *   0.005368 → $0.005368 (only 2 zeros, show normally)
 *   0.0001234 → $0.0001234 (only 3 zeros, show normally)
 *   0.00001234 → $0.0₄1234 (4 zeros, use subscript)
 *   0.00000005274 → $0.0₇5274 (7 zeros, then 5274)
 */
export function formatPrice(value: number | string | null, currencySymbol: string = '$'): string {
  if (value === null) return `${currencySymbol}—`;
  const num = typeof value === 'string' ? parseFloat(value) : value;

  if (num === 0 || isNaN(num)) return `${currencySymbol}0.00`;
  if (num < 0) return `-${formatPrice(-num, currencySymbol)}`;
  if (num >= 1) return `${currencySymbol}${num.toFixed(2)}`;
  if (num >= 0.01) return `${currencySymbol}${num.toFixed(4)}`;

  // Count leading zeros after decimal point
  // Convert to string to analyze digits
  const str = num.toFixed(20); // Get enough precision
  const decimalIndex = str.indexOf('.');
  if (decimalIndex === -1) return `${currencySymbol}${num.toFixed(2)}`;

  // Count zeros after decimal point until first non-zero digit
  let leadingZeros = 0;
  for (let i = decimalIndex + 1; i < str.length; i++) {
    if (str[i] === '0') {
      leadingZeros++;
    } else {
      break;
    }
  }

  // If 3 or fewer leading zeros, show normally with enough precision
  if (leadingZeros <= 3) {
    // Show at least 4 significant digits
    const significantDecimals = leadingZeros + 4;
    return `${currencySymbol}${num.toFixed(significantDecimals)}`;
  }

  // More than 3 leading zeros: use subscript notation
  // Extract significant digits (at least 4)
  const afterZeros = str.slice(decimalIndex + 1 + leadingZeros);
  const significantDigits = afterZeros.slice(0, 4).replace(/0+$/, '') || afterZeros.slice(0, 4);

  return `${currencySymbol}0.0${toSubscript(leadingZeros)}${significantDigits}`;
}

/**
 * Format a USD amount from a bigint scaled to 18 decimals.
 * Truncates to 2 decimal places (no rounding).
 */
export function formatUsdBigint(amount: bigint): string {
  const value = parseFloat(ethers.formatEther(amount));
  if (value === 0) return '0.00';
  if (value < 0.01) return '<0.01';
  const truncated = Math.floor(value * 100) / 100;
  return truncated.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Convert a stWELEPHANT amount to a USD string using the vault exchange rate.
 * Calls previewRedeem to get the underlying WELEPHANT value, then prices it.
 *
 * @param stWelAmount - stWELEPHANT amount (18 decimals)
 * @param welephantPriceUsd - price of 1 WELEPHANT (1e9) in USD, scaled to 18 decimals
 *                            (from swapContract.getWelephantPriceInUSD())
 * @param previewRedeem - converts stWELEPHANT shares to underlying WELEPHANT
 *                        (from stakingContract.previewRedeem)
 * @returns formatted USD string or null if inputs are zero
 */
export async function stWelToUsdViaRedeem(
  stWelAmount: bigint,
  welephantPriceUsd: bigint,
  previewRedeem: (shares: bigint) => Promise<bigint>,
): Promise<string | null> {
  if (welephantPriceUsd === 0n || stWelAmount === 0n) return null;
  const welephantAmount = await previewRedeem(stWelAmount);
  if (welephantAmount === 0n) return null;
  const usdBigint = welephantPriceUsd * welephantAmount / BigInt(10 ** WELEPHANT_DECIMALS);
  return formatUsdBigint(usdBigint);
}

/**
 * Zero address constant
 */
export const ZERO_ADDRESS = ethers.ZeroAddress;

/**
 * Check if an address is the zero address
 */
export function isZeroAddress(value: string): boolean {
  return value === ZERO_ADDRESS || value === '0x0000000000000000000000000000000000000000';
}

/**
 * Format a BNB value (from wei) with compact notation for large numbers
 * Small values show actual decimals, large values use K/M/B suffixes
 */
export function formatCompactBNB(value: bigint | string | number): string {
  const formatted = parseFloat(ethers.formatEther(value));
  if (formatted === 0) return '0';

  // For small values (< 1), show actual value with up to 4 significant decimals
  if (formatted < 1) {
    // Remove trailing zeros but keep meaningful precision
    return formatted.toFixed(4).replace(/\.?0+$/, '');
  }

  // For values >= 1, use compact notation
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 3,
  }).format(formatted);
}

/**
 * Format a WELEPHANT value with compact notation for large numbers
 * Uses 1 decimal place for compact display
 */
export function formatCompactWelephant(value: bigint | string | number): string {
  const formatted = parseFloat(ethers.formatUnits(value, WELEPHANT_DECIMALS));
  if (formatted === 0) return '0';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(formatted);
}

/**
 * Format a count (player count, etc.) with compact notation
 * Uses 1 decimal place for compact display
 */
export function formatCompactCount(value: number | bigint): string {
  const num = typeof value === 'bigint' ? Number(value) : value;
  if (num === 0) return '0';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(num);
}

/**
 * Wait for a transaction to be mined using manual polling.
 * Uses eth_getTransactionReceipt with exponential backoff for reliability.
 *
 * @param tx - The transaction response
 * @param provider - The provider to use for polling
 * @param confirmations - Number of confirmations to wait for (default: 1)
 * @returns The transaction receipt
 */
export async function waitForTransaction(
  tx: TransactionResponse,
  provider: Provider,
  confirmations: number = 1
): Promise<TransactionReceipt | null> {
  const txHash = tx.hash;
  console.log('[waitForTransaction] Polling for tx:', txHash);

  const maxAttempts = 60; // Max ~2-3 minutes with backoff
  const initialDelay = 1000; // Start at 1 second
  const maxDelay = 5000; // Cap at 5 seconds
  const backoffMultiplier = 1.3;

  let attempt = 0;
  let delay = initialDelay;

  while (attempt < maxAttempts) {
    attempt++;
    console.log(`[waitForTransaction] Attempt ${attempt}...`);

    try {
      const receipt = await provider.getTransactionReceipt(txHash);

      console.log(`[waitForTransaction] Attempt ${attempt} result:`, receipt ? 'found' : 'pending');

      if (receipt) {
        // Check if we have enough confirmations
        if (confirmations > 1) {
          const currentBlock = await provider.getBlockNumber();
          const confirmationCount = currentBlock - receipt.blockNumber + 1;

          if (confirmationCount >= confirmations) {
            console.log('[waitForTransaction] Confirmed:', {
              hash: receipt.hash,
              status: receipt.status,
              blockNumber: receipt.blockNumber,
              confirmations: confirmationCount
            });
            return receipt;
          }

          console.log(`[waitForTransaction] ${confirmationCount}/${confirmations} confirmations...`);
        } else {
          console.log('[waitForTransaction] Receipt received:', {
            hash: receipt.hash,
            status: receipt.status,
            blockNumber: receipt.blockNumber
          });
          return receipt;
        }
      }
    } catch (error) {
      console.warn(`[waitForTransaction] Attempt ${attempt} error:`, error);
      // Continue polling on error
    }

    // Wait before next attempt
    await new Promise(resolve => setTimeout(resolve, delay));

    // Exponential backoff with cap
    delay = Math.min(delay * backoffMultiplier, maxDelay);
  }

  console.error(`[waitForTransaction] Timed out after ${maxAttempts} attempts`);
  throw new Error(`Transaction ${txHash} not confirmed after ${maxAttempts} attempts`);
}
